import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { customerApi } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { ProductCard } from "@/components/ProductCard";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { Heart, Share2, Check } from "lucide-react";
import { toast } from "sonner";

const copyLink = async (url) => {
  try {
    if (navigator.share) { await navigator.share({ title: "My Zebaa Wishlist", url }); return "shared"; }
  } catch { /* user cancelled or unsupported */ }
  try {
    await navigator.clipboard.writeText(url);
    return "copied";
  } catch { /* clipboard blocked */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select();
    document.execCommand("copy");
    document.body.removeChild(ta);
    return "copied";
  } catch { return "failed"; }
};

export default function Wishlist() {
  const { wishlist } = useCart();
  const { customer } = useCustomerAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [shared, setShared] = useState(false);

  useEffect(() => {
    if (!customer) { setLoading(false); return; }
    api.get("/products").then(({ data }) => {
      setProducts(data);
      setLoading(false);
    });
  }, [customer]);

  const items = products.filter((p) => wishlist.includes(p.id));

  const handleShare = async () => {
    setSharing(true);
    try {
      const { data } = await customerApi().post("/account/wishlist/share");
      const url = `${window.location.origin}/wishlist/shared/${data.token}`;
      const result = await copyLink(url);
      if (result === "copied") { setShared(true); toast.success("Wishlist link copied — share it with anyone!"); setTimeout(() => setShared(false), 2500); }
      else if (result === "shared") toast.success("Wishlist shared!");
      else toast.error("Could not create the share link. Please try again.");
    } catch {
      toast.error("Could not create the share link. Please try again.");
    } finally {
      setSharing(false);
    }
  };

  // Signed-out gate
  if (customer === false) {
    return (
      <div className="max-w-[1800px] mx-auto px-5 sm:px-8 lg:px-16 py-14 min-h-[70vh] flex items-center justify-center" data-testid="wishlist-gate">
        <div className="text-center max-w-md">
          <Heart className="w-14 h-14 text-pink mx-auto" />
          <h1 className="font-serif text-4xl mt-6 text-charcoal">Your Wishlist</h1>
          <p className="text-gray-500 mt-4">Create an account or sign in to save your favourite pieces and share your wishlist with friends and family.</p>
          <div className="flex items-center justify-center gap-3 mt-8">
            <Link to="/login" data-testid="wishlist-signin-btn" className="bg-pink text-white px-8 py-3.5 text-xs uppercase tracking-[0.18em] hover:bg-pink-hover transition-colors">Sign In</Link>
            <Link to="/register" data-testid="wishlist-register-btn" className="border border-charcoal/20 text-charcoal px-8 py-3.5 text-xs uppercase tracking-[0.18em] hover:bg-charcoal hover:text-white transition-colors">Register</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1800px] mx-auto px-5 sm:px-8 lg:px-16 py-14 min-h-[70vh]" data-testid="wishlist-page">
      <Seo title="My Wishlist" path="/wishlist" noindex />
      <Reveal className="text-center mb-10">
        <p className="text-xs uppercase tracking-[0.28em] text-pink">Saved For Later</p>
        <h1 className="font-serif text-5xl lg:text-6xl mt-3 text-charcoal">My Wishlist</h1>
        <div className="w-16 h-px bg-gold mx-auto mt-6" />
        {items.length > 0 && (
          <button
            onClick={handleShare}
            disabled={sharing}
            data-testid="wishlist-share-btn"
            className="inline-flex items-center gap-2 mt-7 border border-charcoal/20 text-charcoal px-7 py-3 text-xs uppercase tracking-[0.16em] hover:bg-charcoal hover:text-white transition-colors disabled:opacity-50"
          >
            {shared ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
            {shared ? "Link Copied" : sharing ? "Preparing…" : "Share My Wishlist"}
          </button>
        )}
      </Reveal>

      {loading ? (
        <p className="text-center text-gray-400 py-20">Loading your wishlist...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-20" data-testid="wishlist-empty">
          <Heart className="w-14 h-14 text-gray-200 mx-auto" />
          <p className="text-gray-500 mt-5">Your wishlist is empty. Tap the heart on any product to save it here.</p>
          <Link
            to="/shop/all"
            className="inline-block mt-8 bg-pink text-white px-9 py-4 text-xs uppercase tracking-[0.18em] hover:bg-pink-hover transition-colors"
          >
            Explore Collections
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-12">
          {items.map((p, i) => (
            <Reveal key={p.id} delay={(i % 4) * 0.06}>
              <ProductCard product={p} />
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
}
